<template>
  <FieldFrame v-bind="frameBindings">
    <template
      v-for="slotName in forwardedFieldSlots()"
      :key="slotName"
      #[slotName]
    >
      <slot :name="slotName" />
    </template>

    <template
      v-if="hasCharacterLimit(maxlength)"
      #hint
    >
      <FieldCharacterCount
        :value="modelValue"
        :maxlength="maxlength"
      />
    </template>

    <template #default="control">
      <UInput
        ref="input"
        v-bind="{ ...controlAttributes, ...fieldControlAria(control) }"
        :model-value="modelValue"
        :name="name"
        :placeholder="placeholder"
        :disabled="disabled"
        size="md"
        variant="none"
        fixed
        :ui="controlUi"
        @update:model-value="handleUpdate"
        @blur="handleBlur"
      />
    </template>
  </FieldFrame>
</template>

<script setup lang="ts">
import { computed, toRefs, useTemplateRef } from 'vue'
import UInput from '@nuxt/ui/components/Input.vue'
import { createStringInputHandlers } from '~/utils/createStringInputHandlers'

import FieldCharacterCount from '../atoms/FieldCharacterCount/FieldCharacterCount.atom.vue'
import FieldFrame from './FieldFrame/FieldFrame.molecule.vue'
import { useFieldFrame } from './FieldFrame/useFieldFrame'
import { fieldControlAria } from '../base/fieldControlAria'
import { hasCharacterLimit } from '../base/hasCharacterLimit'
import { useFieldPresentation } from '~/design-system/molecules/useFieldPresentation'
import { textEntryFieldDefaults } from '~/design-system/molecules/textEntryFieldDefaults'
import type { TextEntryFieldProps } from '~/interfaces/TextEntryFieldProps'
import type { InputFieldEvents } from '~/interfaces/InputFieldEvents'
import type { FieldLabelSlots } from '~/interfaces/FieldLabelSlots'

const props = withDefaults(
  defineProps<
    TextEntryFieldProps & {
      /** Native input type, e.g. `text` or `email`. */
      type?: string
      /** HTML autocomplete hint assigned to the input. */
      autocomplete?: string
    }
  >(),
  {
    ...textEntryFieldDefaults,
    autocomplete: 'off',
    type: 'text'
  }
)

const emit = defineEmits<InputFieldEvents<string>>()

const NATIVE_TYPE_ATTRIBUTES = {
  email: { autocomplete: 'email', inputmode: 'email', type: 'email' },
  number: { inputmode: 'decimal', type: 'text' },
  url: { autocomplete: 'url', inputmode: 'url', type: 'text' }
} as const

type NativeTypeKey = keyof typeof NATIVE_TYPE_ATTRIBUTES

const isNativeTypeKey = (value: string): value is NativeTypeKey =>
  Object.hasOwn(NATIVE_TYPE_ATTRIBUTES, value)

defineSlots<FieldLabelSlots>()

const { error, help, hint, label, name, required, serverError } = toRefs(props)
const { message, controlUi, inputAttributes } = useFieldPresentation({
  error,
  help,
  required,
  serverError
})
const { frameBindings, forwardedFieldSlots } = useFieldFrame(
  { help, hint, label, name, required },
  message,
  ['help', 'label']
)

// `type=url`/`number` natively block input a server has to accept anyway, so those two
// render `type=text` and lean on `inputmode`/`autocomplete` instead; `email` renders natively.
const nativeAttributes = computed<{
  autocomplete: string | undefined
  inputmode: string | undefined
  type: string
}>(() => {
  if (isNativeTypeKey(props.type)) {
    const mapped = NATIVE_TYPE_ATTRIBUTES[props.type]
    const mappedAutocomplete = 'autocomplete' in mapped ? mapped.autocomplete : undefined
    return {
      autocomplete: props.autocomplete === 'off' ? mappedAutocomplete : props.autocomplete,
      inputmode: mapped.inputmode,
      type: mapped.type
    }
  }
  return { autocomplete: props.autocomplete, inputmode: undefined, type: props.type }
})

// UInput's generated props lack an index signature; one v-bind spread forwards inputmode and aria-required.
const controlAttributes = computed(() => ({
  ...nativeAttributes.value,
  ...inputAttributes.value
}))

// No id reaches UInput on purpose: the field owns the control id. See
// "Let the field own the control id" in docs/design-system-planes.md.

const { handleUpdate, handleBlur } = createStringInputHandlers(emit)

const input = useTemplateRef<{ inputRef?: HTMLInputElement | null }>('input')

defineExpose({
  focus: () => input.value?.inputRef?.focus()
})
</script>
